import React, { useState, useEffect } from 'react';
import { signInWithGoogle } from '../config/Auth';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUltraPassword } from '../services/ultraPassService';

const Login = () => {
  const navigate = useNavigate();
  const { currentUser, firestoreUser, setUltraPasswordAuthenticated, loading } = useAuth();
  const [ultraPasswordInput, setUltraPasswordInput] = useState('');
  const [showUltraPasswordPrompt, setShowUltraPasswordPrompt] = useState(false);
  const [error, setError] = useState('');

  const mainBg = '#1c1c1c';
  const importantText = '#d98d2a';
  const buttonTextColor = '#1c1c1c'; // Dark text on the orange button
  const buttonHoverBg = '#c87d26'; // Slightly darker orange for hover
  const textColor = '#e0e0e0'; // General text color for dark background
  const errorColor = '#ff6b6b'; // Error message color

  useEffect(() => {
    if (loading) return; // Wait for auth state to be determined

    if (currentUser && firestoreUser) {
      if (firestoreUser.authenticated) {
        // Already ultra authenticated, navigate to home
        navigate('/');
      } else {
        // Logged in with Google, but not ultra authenticated, show prompt
        setShowUltraPasswordPrompt(true);
      }
    } else if (currentUser && !firestoreUser) {
      // This case might indicate a delay or error in fetching firestoreUser
      // AuthContext should handle this, but we can show a loading or error state here if needed
      console.log("Waiting for Firestore user profile...");
      // Potentially show a loading indicator specific to this component
    } else {
      // No currentUser, so ensure ultra password prompt is hidden
      setShowUltraPasswordPrompt(false);
    }
  }, [currentUser, firestoreUser, navigate, loading]);

  const handleGoogleLogin = async () => {
    setError('');
    try {
      await signInWithGoogle();
      // AuthContext's onAuthStateChanged will trigger handleUserLogin
      // and update firestoreUser, which useEffect will then react to.
    } catch (error) {
      console.error('Google Login failed:', error);
      setError('Google login failed. Please try again.');
    }
  };

  const handleUltraPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!currentUser) {
      setError("User not logged in. Please sign in with Google first.");
      return;
    }
    try {
      const passwords = await getUltraPassword();
      
      // Check if input matches either password or password2
      const isValid = ultraPasswordInput === passwords.password || 
                     (passwords.password2 && ultraPasswordInput === passwords.password2);
      
      if (isValid) {
        await setUltraPasswordAuthenticated(true); // This now updates Firestore via AuthContext
        // Navigation will be handled by useEffect reacting to firestoreUser.authenticated change
      } else {
        setError('Incorrect secret password.');
        await setUltraPasswordAuthenticated(false); // Ensure status is false in Firestore
      }
    } catch (err) {
      console.error('Error validating ultra password:', err);
      setError('Error validating secret password. Please try again.');
      // Consider if setUltraPasswordAuthenticated(false) should be called here too
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: mainBg, color: textColor }}>
        <Typography>Loading authentication status...</Typography>
      </div>
    );
  }

  if (!showUltraPasswordPrompt) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        backgroundColor: mainBg, 
        fontFamily: '"Georgia", serif', 
        padding: '20px'
      }}>
        <Typography variant="h4" sx={{ color: importantText, marginBottom: '20px', fontFamily: 'inherit' }}>
          Welcome, Adventurer!
        </Typography>
        <Button
          variant="contained"
          startIcon={<AutoStoriesIcon />} 
          onClick={handleGoogleLogin}
          sx={{
            backgroundColor: importantText, 
            color: buttonTextColor, 
            textTransform: 'none',
            fontWeight: 'bold',
            fontFamily: 'inherit',
            padding: '12px 24px',
            borderRadius: '8px',
            border: `2px solid ${buttonTextColor}`,
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: buttonHoverBg,
              boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
            },
          }}
        >
          Begin Your Adventure with Google
        </Button>
        {error && <Typography color="error" sx={{ marginTop: 2, color: errorColor }}>{error}</Typography>}
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh', 
      backgroundColor: mainBg, 
      fontFamily: '"Georgia", serif', 
      padding: '20px' 
    }}>
      <Typography variant="h5" sx={{ color: importantText, marginBottom: '20px', fontFamily: 'inherit' }}>
        Enter the Secret Scroll's Word
      </Typography>
      <Box component="form" onSubmit={handleUltraPasswordSubmit} sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          width: '100%', 
          maxWidth: '300px' 
      }}>
        <TextField
          type="password"
          label="Secret Word"
          variant="outlined"
          value={ultraPasswordInput}
          onChange={(e) => setUltraPasswordInput(e.target.value)}
          required
          sx={{
            marginBottom: 2,
            width: '100%',
            input: { color: textColor, fontFamily: 'inherit' },
            label: { color: importantText, fontFamily: 'inherit' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: importantText,
              },
              '&:hover fieldset': {
                borderColor: buttonHoverBg,
              },
              '&.Mui-focused fieldset': {
                borderColor: buttonHoverBg,
              },
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={<LockIcon />}
          sx={{
            backgroundColor: importantText, 
            color: buttonTextColor, 
            textTransform: 'none',
            fontWeight: 'bold',
            fontFamily: 'inherit',
            padding: '10px 20px',
            borderRadius: '8px',
            border: `2px solid ${buttonTextColor}`,
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: buttonHoverBg,
              boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
            },
            width: '100%'
          }}
        >
          Unlock Full Access
        </Button>
        {error && <Typography color="error" sx={{ marginTop: 2, color: errorColor }}>{error}</Typography>}
      </Box>
    </div>
  );
};

export default Login;
